<template>
  <section
    class="relative bg-background-02 px-6"
    :class="[verticalTopSpacing, verticalBottomSpacing]"
  >
    <div class="absolute inset-0 max-h-[68.0625rem]">
      <BgBlockPink class="h-full w-full"></BgBlockPink>
    </div>

    <div
      class="relative mx-auto max-w-[80rem] space-y-6 rounded-[0.9375rem] rounded-tl-none border border-border-light bg-white px-4 py-8 shadow-md lg:space-y-8 lg:p-10"
    >
      <section class="flex flex-col gap-6 lg:flex-row">
        <BaseInputDropdown
          v-model="selectedProductType"
          class="w-full lg:max-w-[17.5rem]"
          label="Type product"
          :has-label="true"
          :options="optionsProduct"
          placeholder="Selecteer type product"
        ></BaseInputDropdown>
        <BaseInputDropdown
          v-model="selectedRepayment"
          class="w-full lg:max-w-[17.5rem]"
          label="Type aflossing"
          :has-label="true"
          :options="optionsRepayment"
          placeholder="Selecteer type aflossing"
          :disabled="!optionsRepayment.length"
        ></BaseInputDropdown>
        <BaseInputDropdown
          v-model="selectedPeriod"
          class="w-full lg:max-w-[17.5rem]"
          label="Periode"
          :has-label="true"
          :options="optionsPeriods"
          placeholder="Selecteer periode"
          :disabled="!optionsPeriods.length"
        ></BaseInputDropdown>
      </section>

      <section>
        <div id="print-section" class="w-full overflow-x-auto">
          <table class="w-full text-text-dark">
            <thead>
              <tr class="border-b-2 border-b-text-dark">
                <th class="sticky left-0 min-w-20 bg-white py-4 text-left text-sm font-bold">
                  {{ table.headers[0] }}
                </th>
                <th
                  v-for="(h, i) in table.headers.slice(1)"
                  :key="i"
                  class="px-8 py-4 text-center text-sm font-bold"
                >
                  {{ h }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, ridx) in displayedRows"
                :key="ridx"
                :class="['odd:bg-background-01']"
              >
                <td
                  class="sticky left-0 min-w-20 py-4 pl-2 text-left text-sm font-bold"
                  :class="[ridx % 2 === 0 ? 'bg-background-01' : 'bg-white']"
                >
                  {{ row.duration }}
                </td>
                <td
                  v-for="(cell, cidx) in row.values"
                  :key="cidx"
                  class="px-8 py-4 text-center text-sm"
                >
                  {{ cell }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="flex flex-wrap items-center justify-center gap-x-2 gap-y-4 py-3">
          <BaseButton
            variant="secondary"
            :text="showAllRows ? 'Toon minder rentetarieven' : 'Toon alle rentetarieven'"
            :is-disabled="table.rows.length <= 5"
            @click="toggleShowRows"
          />
          <BaseButton
            variant="secondary"
            text="Print rentetarieven"
            @click="handlePrint"
          ></BaseButton>
        </div>
      </section>

      <section class="rte-table pl-4 lg:pl-0" v-html="disclaimer"></section>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { usePaperizer } from 'paperizer'
import type { IBlockTable, Option, TableData } from '@/features/block-table/types/IBlockTable.ts'
import BaseButton from '@/common/components/base/BaseButton/BaseButton.vue'
import BaseInputDropdown from '@/common/components/base/BaseInputDropdown/BaseInputDropdown.vue'
import type { IBaseInputDropdownOptions } from '@/common/components/base/BaseInputDropdown/IBaseInputDropdown.ts'
import BgBlockPink from '/public/backgrounds/background-block-pink.svg'

const {
  productId,
  productTypes: initialProductTypes,
  verticalTopSpacing,
  verticalBottomSpacing,
  disclaimer,
} = defineProps<IBlockTable>()

const optionsProduct = ref<Option[]>(initialProductTypes)
const optionsRepayment = ref<Option[]>([])
const optionsPeriods = ref<Option[]>([])

const selectedProductType = ref<IBaseInputDropdownOptions | null>(initialProductTypes[0] ?? null)
const selectedRepayment = ref<IBaseInputDropdownOptions | null>(null)
const selectedPeriod = ref<IBaseInputDropdownOptions | null>(null)

const table = ref<TableData>({ headers: [], rows: [] })
const showAllRows = ref(false)

const displayedRows = computed(() => {
  const allRows = table.value.rows

  if (showAllRows.value || allRows.length <= 5) return allRows

  return table.value.rows.filter(row => {
    const duration = row.duration.toLowerCase()
    if (duration.startsWith('variabel')) return true

    const startsWithNumber = duration.match(/^(\d+)/)
    if (!startsWithNumber) return false

    const num = parseInt(startsWithNumber[1], 10)
    return num === 1 || num % 5 === 0
  })
})

watch(
  selectedProductType,
  async pt => {
    if (!pt) return
    const res = await fetch(`/api/products/${productId}/rates/${pt.value}/meta`)
    if (!res.ok) return

    const { repaymentTypes, periods } = (await res.json()) as {
      repaymentTypes: Option[]
      periods: Option[]
    }

    optionsRepayment.value = repaymentTypes
    optionsPeriods.value = periods

    selectedRepayment.value = repaymentTypes[0] ?? null
    selectedPeriod.value = periods[0] ?? null
  },
  { immediate: true, flush: 'post' },
)

watch(
  [selectedRepayment, selectedPeriod],
  async ([repayment, period]) => {
    if (!repayment || !period) return

    const url = `/api/products/${productId}/rates/${period.value}/repaymentTypes/${repayment.value}`
    const res = await fetch(url)
    if (res.ok) {
      table.value = (await res.json()) as TableData
    }
  },
  { immediate: true, flush: 'post' },
)

const toggleShowRows = () => {
  showAllRows.value = !showAllRows.value
}

const { paperize } = usePaperizer('print-section', {
  styles: ['/css/print-rates.css'],
  bodyClass: 'print-rates',
})
const handlePrint = () => {
  paperize()
}
</script>
